import { OverlayTrigger, Tooltip } from "@rio-cloud/rio-uikit";
import RadioButton from "@rio-cloud/rio-uikit/RadioButton";
import Checkbox from "@rio-cloud/rio-uikit/Checkbox";
import Map from "@rio-cloud/rio-uikit/Map";
import Marker from "@rio-cloud/rio-uikit/Marker";
import SingleMapMarker from "@rio-cloud/rio-uikit/SingleMapMarker";
import { useState } from "react";

import { getDistanceBetweenTwoLocations } from "../../../../../../utils/locationUtils";
import { Dealership } from "../../../../../../models/dealership";
import { Asset } from "../../../../../../models/asset";
import { useSchedulingFormDealershipGroup } from "../../../../../../hooks/useSchedulingForm";
import { useIntl } from "react-intl";
import AddressSearch from "../../../../../../components/form/AddressSearch";
import { useMediaQuery } from "@mui/material";

interface IGroupConsultantDealershipFormProps {
  asset?: Asset;
  setAsset: (asset: Asset) => void;
  setStep: (step: number) => void;
  dealership?: Dealership;
  setDealership: (dealership: Dealership) => void;
}

const GroupConsultantDealershipForm = (props: IGroupConsultantDealershipFormProps) => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id: `intl-msg:appointment.step2.${id}` });

  const {
    assetGeoLocation,
    activeTab,
    setActiveTab,
    enableLocation,
    handleSetEnableLocation,
    dealerships,
    searchDealershipTerm,
    setSearchDealershipTerm,
    addressForm,
    setAddressForm,
    setAssetGeoLocation,
  } = useSchedulingFormDealershipGroup(props.dealership, props.asset, props.setAsset);

  const isResponsiveView = useMediaQuery("(max-width: 1024px)");
  const [showMap, setShowMap] = useState(true);

  return (
    <div className={`${isResponsiveView ? "flex flex-col" : "flex"} chooseConcessionaria ${isResponsiveView ? "h-screen" : "h-[85vh]"}`}>
      {isResponsiveView && (
        <div className="bg-white dark:bg-neutral-900 z-10 shadow-md">
          <div className="sticky top-0 flex justify-center items-center p-2">
            <button
              onClick={() => setShowMap(false)}
              className={`px-6 py-2 mr-2 font-medium rounded-l-md transition ${!showMap ? "text-black dark:text-neutral-100 shadow-md" : "bg-gray-200 dark:bg-neutral-700 dark:text-neutral-300"}`}
            >
              Formulário
            </button>
            <button
              onClick={() => setShowMap(true)}
              className={`px-6 py-2 font-medium rounded-r-md transition ${showMap ? "text-black dark:text-neutral-100 shadow-md" : "bg-gray-200 dark:bg-neutral-700 dark:text-neutral-300"}`}
            >
              Mapa
            </button>
          </div>
        </div>
      )}

      <div className={`${isResponsiveView ? (showMap ? "hidden" : "flex flex-col w-full") : "w-1/3 flex flex-col"} p-10 ${isResponsiveView ? "pb-24" : "padding-right-20"} overflow-auto`}>
        <h2 className="text-size-h2 text-color-black font-semibold text-uppercase">
          2. {t('title')}
        </h2>
        <p className="text-color-status-resting text-size-16 padding-top-10">
          {t('subtitle')}
        </p>

        <div>{t('vehicleLocation')}</div>
        <div className="padding-x-10 padding-y-20">
          <Checkbox
            checked={enableLocation}
            onChange={() => handleSetEnableLocation(!enableLocation)}
          >
            {t('manualLocation')}
          </Checkbox>
        </div>

        <div>{t('geolocation')}</div>
        <div className="flex justify-between gap-6">
          <div className="form-group w-1/2">
            <label htmlFor="lat">{t('latitude')}</label>
            <input
              id="lat"
              placeholder="Latitude"
              value={
                (enableLocation
                  ? props.asset?.geoLocation?.lat
                  : assetGeoLocation?.lat) || "-"
              }
              className="form-control"
              disabled
            />
          </div>
          <div className="form-group w-1/2">
            <label htmlFor="lng">{t('longitude')}</label>
            <input
              id="lng"
              placeholder="Longitude"
              value={
                (enableLocation
                  ? props.asset?.geoLocation?.lng
                  : assetGeoLocation?.lng) || "-"
              }
              className="form-control"
              disabled
            />
          </div>
        </div>

        <div>
          <div>{t('vehicleLocation')}</div>
          <div className="form-group">
            <label htmlFor="address">{t('addressLabel')}</label>
            <AddressSearch
              value={`${[addressForm.address, addressForm.neighborhood, addressForm.city, addressForm.state].filter(Boolean).join(", ")}`}
              getAddress={async (address) => {
                setAddressForm({
                  ...addressForm,
                  address: address.street,
                  neighborhood: address.district,
                  city: address.city,
                  state: address.state
                });
                setAssetGeoLocation(address.geoLocation);
              }}
              disabled={!enableLocation}
            />
          </div>
        </div>

        <div className="new-concessionaria">
          <div className="flex">
            <div className="form-group w-full">
              <label htmlFor="dealershipSearch">{t('selectDealership')}</label>

              <div className={`search-concessionaria ${isResponsiveView ? "mb-30" : "mb-52"}`}>
                <div className="form-group form-group-lg">
                  <div className="input-group">
                    <span className="input-group-addon">
                      <span className="rioglyph rioglyph-search"></span>
                    </span>
                    <input
                      id="dealershipSearch"
                      className="form-control"
                      type="text"
                      placeholder={t('searchPlaceholder')}
                      onChange={(e) => setSearchDealershipTerm(e.target.value)}
                      value={searchDealershipTerm}
                    />
                  </div>
                </div>

                {dealerships.length === 0 && (
                  <div className="flex justify-center padding-top-10">
                    <span className="text-color-gray">
                      {t('noDealershipFound')}
                    </span>
                  </div>
                )}

                <div className={`${isResponsiveView ? "max-h-[60vh] overflow-y-auto" : ""}`}>
                  {dealerships
                    .map((foundDealership: Dealership) => {
                      foundDealership.distance = getDistanceBetweenTwoLocations(
                        props.asset?.geoLocation ?? { lat: -23.550573750495325, lng: -46.64411772565403 },
                        foundDealership?.geoLocation
                      );
                      return foundDealership;
                    })
                    .sort((a, b) => (a.distance ?? 0) - (b.distance ?? 0))
                    .map((foundDealership: Dealership) => (
                      <div
                        key={foundDealership.id}
                        className={`flex concessionaria-options w-full ${isResponsiveView ? "flex-wrap" : ""} ${foundDealership.id === props.dealership?.id ? "selected" : ""}`}
                      >
                        <RadioButton
                          name="radios"
                          id={`radio-${foundDealership.id}`}
                          value={foundDealership.id}
                          checked={foundDealership.id === props.dealership?.id}
                          onChange={() => props.setDealership(foundDealership)}
                        />
                        <div className={`${isResponsiveView ? "w-[80%]" : "w-[90%]"}`}>
                          <div className={`flex ${isResponsiveView ? "flex-col" : "items-center"}`}>
                            <strong className={`text-size-14 ${isResponsiveView ? "block" : ""}`}>
                              {foundDealership.name}
                            </strong>
                            <span className={`card-km text-size-14 font-semibold ${isResponsiveView ? "ml-0 mt-1" : "ml-2"}`}>
                              {foundDealership.distance} Km
                            </span>
                          </div>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-${foundDealership.id}`} allowOnTouch>
                                <div className="flex flex-col padding-10">
                                  <p>
                                    {[
                                      foundDealership.address,
                                      foundDealership.neighborhood,
                                      foundDealership.city,
                                      foundDealership.state,
                                    ]
                                      .filter(Boolean)
                                      .join(", ")}
                                  </p>
                                </div>
                              </Tooltip>
                            }
                          >
                            <span className="text-size-14 truncate block">
                              {[foundDealership.city, foundDealership.state]
                                .filter(Boolean)
                                .join(", ")}
                            </span>
                          </OverlayTrigger>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {!isResponsiveView && (
        <div className="w-1/3 info-and-nav padding-bottom-20">
          <div className="page-info flex justify-between padding-15">
            <span className="text-color-status-resting font-size-14">
              {t('stepLabel')} <strong className="text-color-danger">2</strong> {t('ofLabel')} 4
            </span>
            <span className="text-color-status-resting font-size-14">
              {t('stepInfo')}
            </span>
          </div>
          <div className="flex justify-between navigation-buttons">
            <button onClick={() => props.setStep(1)}>
              <span className="rioglyph rioglyph-arrow-left text-size-16"></span>{" "}
              {t('back')}
            </button>
            <button
              onClick={() => props.setStep(3)}
              className="px-4 py-2 btn btn-primary"
              disabled={!props.dealership || props.dealership.id === ""}
            >
              {t('next')}
            </button>
          </div>
        </div>
      )}

      {isResponsiveView && (
        <div className="fixed bottom-0 left-[50px] right-0 bg-white dark:bg-neutral-900 border-t dark:border-neutral-700 shadow-md p-4 z-20">
          <div className="flex justify-between items-center">
            <button onClick={() => props.setStep(1)} className="flex items-center">
              <span className="rioglyph rioglyph-arrow-left text-size-16 mr-1"></span>
              {t('back')}
            </button>
            <div className="text-center text-color-status-resting text-md">
              {t('stepLabel')} <strong className="text-color-danger">2</strong> {t('ofLabel')} 4
            </div>
            <button
              onClick={() => props.setStep(3)}
              className="px-4 py-2 btn btn-primary text-md"
              disabled={!props.dealership || props.dealership.id === ""}
            >
              {t('next')}
            </button>
          </div>
        </div>
      )}

      <div className={`${isResponsiveView ? (showMap ? "w-full" : "hidden") : "w-2/3"} ${isResponsiveView ? "h-[calc(85vh-48px)]" : "h-full"}`}>
        <div className="w-full h-full">
          <Map
            credentials={{ apikey: import.meta.env.VITE_HERE_MAPS_API_KEY }}
            center={{
              lat: enableLocation
                ? props.asset?.geoLocation?.lat || -23.550573750495325
                : assetGeoLocation?.lat || -23.550573750495325,
              lng: enableLocation
                ? props.asset?.geoLocation?.lng || -46.64411772565403
                : assetGeoLocation?.lng || -46.64411772565403,
            }}
            zoom={10}
            hideMapSettings
          >
            <Marker
              position={{
                lat: enableLocation
                  ? props.asset?.geoLocation?.lat || -23.550573750495325
                  : assetGeoLocation?.lat || -23.550573750495325,
                lng: enableLocation
                  ? props.asset?.geoLocation?.lng || -46.64411772565403
                  : assetGeoLocation?.lng || -46.64411772565403,
              }}
              icon={
                <SingleMapMarker
                  iconNames={["truck"]}
                  markerColor="bg-map-marker-asset"
                  active
                />
              }
            />
            {dealerships
              .filter(
                (dealership: Dealership) =>
                  dealership.geoLocation?.lat && dealership.geoLocation?.lng
              )
              .map((dealership: Dealership) => (
                <Marker
                  key={dealership.id}
                  position={{
                    lat: dealership.geoLocation?.lat ?? 0,
                    lng: dealership.geoLocation?.lng ?? 0,
                  }}
                  icon={
                    <SingleMapMarker
                      iconNames={["factory"]}
                      markerColor="bg-map-marker-poi"
                      active
                      name={dealership.name}
                    />
                  }
                />
              ))}
          </Map>
        </div>
      </div>
    </div>
  );
};

export default GroupConsultantDealershipForm;
